
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import { Team, CaseDetails } from "@/types"
import User from "@/types/user"
import { InsightCalculation, InsightCalculationTotal, LabelFormat } from "@/types/insights"

// @ts-ignore
import { GChart } from "vue-google-charts"
import moment from "moment"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"

import ChartOptions from "./ChartOptions.vue"
import vSelect from "vue-select"

import api from "@/services/api"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"chart": GChart,
		"chart-options": ChartOptions
	}
})
export default class ChartResults extends Vue {
	@Getter("getMe") public me!: User;

	@Getter("getScreenWidth") public screenWidth!: number;

	@Prop()
	resultGroups: any;

	@Prop()
	columns: any;

	@Prop({ default: () => [] })
	calculations!: InsightCalculation[];

	@Prop({ default: () => null })
	graphCalculations!: string[] | null;

	@Prop({ default: "ColumnChart" })
	chartType: any;

	@Prop({ default: LabelFormat.Text })
	labelFormat!: LabelFormat;

	@Prop({ default: true })
	showChartOptions!: boolean;

	@Prop({ default: function () { return [] } })
	chartConfiguration!: any;

	useValueLabels: boolean = false
	singleCalculation: InsightCalculation | null = null;
	includedCalculations: InsightCalculation[] = [];

	chartOptions: any = {
		backgroundColor: "transparent",
		legend: {
			position: "top"
		},
		width: "100%",
		height: "100%"
	}

	get displayCalculations () {
		if (this.chartType === "PieChart") {
			return this.singleCalculation ? [this.singleCalculation] : []
		} else if (this.graphCalculations) {
			return this.calculations.filter(c => this.graphCalculations!.includes(c.key))
		} else {
			return this.includedCalculations
		}
	}

	get pieCalculations () {
		if (!this.calculations) return []
		return this.calculations.filter(c => !c.groupBy)
	}

	get configuredChartOptions () {
		this.useValueLabels = false

		const graphOptions = this.chartConfiguration.graphOptions

		if (graphOptions === "labels") {
			this.useValueLabels = true
		}

		// Check the appropriate checkbox options rendered with ChartOptions
		// and send the user selected choice to google charts
		switch (this.chartType) {
			case "ColumnChart":
				switch (true) {
					case graphOptions === "column" || !graphOptions:
						return {
							...this.chartOptions,
							isStacked: false
						}
					case graphOptions === "stacked":
						return {
							...this.chartOptions,
							isStacked: true
						}
					case graphOptions === "percent":
						return {
							...this.chartOptions,
							isStacked: "percent"
						}
					default:
						return {
							...this.chartOptions
						}
				}
			case "ComboChart":
				return {
					...this.chartOptions,
					seriesType: "bars",
					series: {
						[graphOptions]: {
							type: "line",
							curveType: "function"
						}
					}
				}
			case "LineChart":
				switch (true) {
					case graphOptions === true:
						return {
							...this.chartOptions,
							curveType: "function"
						}
					default:
						return {
							...this.chartOptions,
							curveType: "none"
						}
				}
			default:
			// return default options for non monitored chart types
				return {
					...this.chartOptions,
					...this.chartConfiguration
				}
		}
	}

	get chartData () {
		if (!this.resultGroups || !this.calculations || this.displayCalculations.length === 0) {
			return []
		}

		const header = [
			"Header",
			...this.displayCalculations.map(calculation => {
				if (calculation && calculation.groupBy && calculation.groupByKeyLabels) {
					const calcKeys = Object.keys(calculation.groupByKeyLabels)
					var groupedHeaders: any[] = calcKeys.map(k => calculation.groupByKeyLabels![k])

					if (this.useValueLabels) {
						groupedHeaders = groupedHeaders.flatMap(h => [h, { role: "annotation" }])
					}
					return groupedHeaders
				} else {
					var headers: any[] = [calculation.label]

					if (this.useValueLabels) {
						headers.push({ role: "annotation" })
					}
					return headers
				}
			}).flat()
		]

		const values = this.resultGroups.map((group: any) => {
			return [
				formatFieldsMixin.methods.formatLabel(this.labelFormat, group.label),
				...this.displayCalculations.map(calculation => {
					if (calculation && calculation.groupBy && calculation.groupByKeyLabels) {
						const calcKeys = Object.keys(calculation.groupByKeyLabels)
						var groupedVals = calcKeys.map(
							k => group.calculations
								? group.calculations[calculation.key] && group.calculations[calculation.key][k]
									? group.calculations[calculation.key][k]
									: 0
								: 0
						)

						if (this.useValueLabels) {
							groupedVals = groupedVals.flatMap(v => [v, v])
						}
						return groupedVals
					} else {
						var vals = [group.calculations ? group.calculations[calculation.key] || 0 : 0]

						if (this.useValueLabels) {
							vals.push(group.calculations ? group.calculations[calculation.key] || 0 : 0)
						}
						return vals
					}
				}).flat()
			]
		})

		return [
			header,
			...values
		]
	}

	private mounted () {
		if (this.calculations.length > 0) {
			if (this.includedCalculations.length === 0) {
				this.includedCalculations.push(this.calculations[0])
			}
			if (!this.singleCalculation && this.pieCalculations.length > 0) {
				this.singleCalculation = this.pieCalculations[0]
			}
		}
	}
}

